<script setup>
/**
 * Activity — what the assistant actually did. THE PLAN, §4 P9.
 *
 * This is the old Reports tab, renamed and rewritten, and the rename is the smaller half
 * of the change. Chatwoot already owns a top-level *Reports* row in the primary sidebar,
 * and two rows called Reports in one sidebar is a defect on its own — but the reason this
 * screen needed rebuilding is what it used to contain, not what it was called.
 *
 * ── CREDITS ARE NOT ON THIS PAGE, AND THAT IS THE POINT ────────────────────────────────
 * The balance, the next expiry and the low-balance warning live on Overview. Billing state
 * is not a report: an owner asking "am I about to run out" is asking about right now, and
 * the old build made her open a report and scroll past four regions to find out — the plan
 * measures that click path at ten. Nothing here takes a credit figure, and the BFF drops
 * `creditsUsed` from the summary rather than merely leaving it unrendered, because a field
 * that reaches the browser is a field a future screen renders.
 *
 * ── FOUR REGIONS, IN THE ORDER AN OWNER ASKS THEM ─────────────────────────────────────
 *   1. the numbers — how much happened, and how it compares to the period before;
 *   2. day by day — the series those numbers are literally summed from;
 *   3. what it could not answer — the only region with a verb, because it is the only one
 *      she can do something about;
 *   4. what it actually said — the conversations themselves, opened in place.
 *
 * ── ONE READ, ONE WINDOW ──────────────────────────────────────────────────────────────
 * All four regions come from ONE request, so they cannot describe different windows. The
 * BFF sums the headline totals from the same series this page draws as bars, which is what
 * stops "71 conversations" sitting above bars that add up to 68 — a real hazard, because
 * the sidecar's summary and its series each compute a window from their own clock and
 * disagree around midnight UTC.
 *
 * ── THE RANGE LIVES IN THE URL ────────────────────────────────────────────────────────
 * `…/waslo/activity?range=30` (THE PLAN §2), so a period is a link an owner can bookmark
 * and send to whoever asked her the question. Changing it does NOT blank the page: the
 * composable keeps the data it has while it refetches, and the header says "Updating…" —
 * a report that flashes empty every time you touch a control teaches you not to touch it.
 */
import { computed, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import SettingsHeader from 'dashboard/components-next/captain/pageComponents/settings/SettingsHeader.vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';

import MetricCard from '../components/MetricCard.vue';
import TranscriptRow from '../components/TranscriptRow.vue';
import TrendChart from '../components/TrendChart.vue';
import UnansweredList from '../components/UnansweredList.vue';
import WasloExplainer from '../components/WasloExplainer.vue';
import WasloPageLayout from '../components/WasloPageLayout.vue';
import { useWasloI18n } from '../i18n.js';
import { useWasloApi } from '../composables/useWasloApi.js';
import { useWasloAccountId } from '../composables/useWasloAccountId.js';
import { toIntlLocale } from '../helpers/intl.js';
import { WASLO_ACTIVITY_RANGES, wasloApi } from '../api/client.js';

const { t, locale } = useWasloI18n();

const accountId = useWasloAccountId();
const route = useRoute();
const router = useRouter();

/**
 * The same default the BFF applies to a call with no `range`. Written down on both sides
 * on purpose: a client-side default that silently disagreed with the server's would make
 * the chips and the data describe different windows on first paint, and nothing on screen
 * would say so. `specs/activity.spec.js` pins this number against the BFF's.
 */
const DEFAULT_RANGE = 30;

/** A URL is user input. Anything that is not one of ours falls back, never 400s. */
const range = computed(() => {
  const raw = Number(route?.query?.range);
  return WASLO_ACTIVITY_RANGES.includes(raw) ? raw : DEFAULT_RANGE;
});

const report = useWasloApi(signal =>
  wasloApi.getActivityReport(accountId.value, range.value, signal)
);

// `range` is read inside the loader, so re-running it is the whole of "change the period".
// `useWasloApi` aborts the in-flight request first, so clicking 7 → 30 → 90 quickly cannot
// land the 7-day answer on top of the 90-day one.
watch(range, () => report.reload());

const reportData = computed(() => report.data.value);
const totals = computed(() => reportData.value?.totals ?? null);
const previous = computed(() => reportData.value?.previous ?? null);

const errorMessage = computed(() => report.errorMessage.value);

// The composable's flag: it counts IDLE, which is the state this page is in on its very
// first frame, before `onMounted` has run. Testing `state === LOADING` here is what put a
// row of empty tiles and an empty chart on screen before a single request had been made.
const isFetching = report.isFetching;

/** A refetch over data we already have. Not a loading screen — a line of text. */
const isRefreshing = computed(
  () => report.isLoading.value && report.hasLoaded.value
);

// ── the period ─────────────────────────────────────────────────────────────────────
const intlLocale = computed(() => toIntlLocale(locale.value));

const rangeTabs = computed(() =>
  WASLO_ACTIVITY_RANGES.map(days => ({
    value: days,
    label: t('WASLO.ACTIVITY.RANGE.DAYS', { days }),
  }))
);

const activeRangeIndex = computed(() =>
  WASLO_ACTIVITY_RANGES.indexOf(range.value)
);

// `replace`, not `push`: the period is a view of one screen, not a place you visited, and
// pushing it would make Back walk through every chip the owner tried instead of leaving
// the section.
const selectRange = tab => {
  if (!tab || tab.value === range.value) return;
  router.replace({
    query: { ...(route?.query ?? {}), range: String(tab.value) },
  });
};

// ── the numbers ────────────────────────────────────────────────────────────────────
const formatCount = value =>
  new Intl.NumberFormat(intlLocale.value).format(value);

const formatPercent = fraction =>
  new Intl.NumberFormat(intlLocale.value, {
    style: 'percent',
    maximumFractionDigits: 0,
  }).format(fraction);

const directionOf = (current, before) => {
  if (current > before) return 'up';
  if (current < before) return 'down';
  return 'same';
};

const conversationsCompare = computed(() =>
  previous.value
    ? t('WASLO.ACTIVITY.NUMBERS.WAS', {
        value: formatCount(previous.value.conversations),
        days: range.value,
      })
    : ''
);

/**
 * A rate over an empty window is not a rate. `handoffRate` is handovers ÷ conversations,
 * so a period in which nobody wrote in comes back as 0 — and "Was 0% in the 30 days
 * before" reads as "your assistant handled everything itself last month", which is the
 * opposite of what happened. So the comparison is only made when there was something to
 * compare.
 */
const hasComparableRate = computed(
  () => (previous.value?.conversations ?? 0) > 0
);

const handoffCompare = computed(() => {
  if (!previous.value) return '';
  return hasComparableRate.value
    ? t('WASLO.ACTIVITY.NUMBERS.WAS', {
        value: formatPercent(previous.value.handoffRate),
        days: range.value,
      })
    : t('WASLO.ACTIVITY.NUMBERS.NO_COMPARE');
});

const handoffHint = computed(() =>
  totals.value
    ? t('WASLO.ACTIVITY.NUMBERS.HANDOFFS_HINT', {
        handoffs: formatCount(totals.value.handoffConversations),
        total: formatCount(totals.value.conversations),
      })
    : ''
);

// ── the conversations ──────────────────────────────────────────────────────────────
// One row open at a time, and the newest one opens by itself. Reading transcripts is why
// an owner comes to this region, and a stack of identical closed rows is a filing cabinet:
// it makes her click once to find out whether the thing is even worth reading. The newest
// conversation is the one she would have opened anyway, so it is already open — and
// because a transcript is fetched only when its row is open (`TranscriptRow`), that costs
// exactly one extra request and nothing at all if she never scrolls this far.
const openConversationId = ref(null);

watch(reportData, data => {
  openConversationId.value = data?.conversations?.[0]?.id ?? null;
});

const toggleConversation = id => {
  openConversationId.value = openConversationId.value === id ? null : id;
};
</script>

<template>
  <WasloPageLayout
    :header-title="t('WASLO.ACTIVITY.TITLE')"
    :is-fetching="isFetching"
    :not-provisioned="report.isNotProvisioned.value"
    :error-message="errorMessage"
    @retry="report.reload()"
  >
    <!-- Outside the state branches on purpose: the period control stays put while the
         page below it reloads, so the thing you just clicked never disappears. -->
    <template #controls>
      <div class="flex flex-wrap items-center gap-3 pb-4">
        <TabBar
          :tabs="rangeTabs"
          :initial-active-tab="activeRangeIndex"
          :aria-label="t('WASLO.ACTIVITY.RANGE.LABEL')"
          data-waslo-range
          @tab-changed="selectRange"
        />
        <span
          v-if="isRefreshing"
          class="text-sm text-n-slate-11"
          data-waslo-range-updating
        >
          {{ t('WASLO.ACTIVITY.RANGE.UPDATING') }}
        </span>
      </div>
    </template>

    <template #body>
      <div v-if="reportData" class="flex flex-col gap-8 pb-10">
        <WasloExplainer
          icon="i-lucide-chart-no-axes-column"
          :note="t('WASLO.ACTIVITY.SPOTLIGHT')"
        />

        <!-- 1 · HOW MUCH HAPPENED. -->
        <section class="grid gap-4 sm:grid-cols-3" data-waslo-activity-numbers>
          <MetricCard
            :label="t('WASLO.ACTIVITY.NUMBERS.CONVERSATIONS')"
            :value="formatCount(totals.conversations)"
            :hint="t('WASLO.ACTIVITY.NUMBERS.CONVERSATIONS_HINT')"
            :compare="conversationsCompare"
            :direction="
              directionOf(totals.conversations, previous.conversations)
            "
            data-waslo-metric-conversations
          />
          <MetricCard
            :label="t('WASLO.ACTIVITY.NUMBERS.HANDOFFS')"
            :value="formatPercent(totals.handoffRate)"
            :hint="handoffHint"
            :compare="handoffCompare"
            :direction="
              hasComparableRate
                ? directionOf(totals.handoffRate, previous.handoffRate)
                : ''
            "
            data-waslo-metric-handoffs
          />
          <MetricCard
            :label="t('WASLO.ACTIVITY.NUMBERS.UNANSWERED')"
            :value="formatCount(totals.unansweredCount)"
            :hint="t('WASLO.ACTIVITY.NUMBERS.UNANSWERED_HINT')"
            data-waslo-metric-unanswered
          />
        </section>

        <!-- 2 · THE SERIES THOSE NUMBERS ARE SUMMED FROM. -->
        <section class="flex flex-col gap-4">
          <SettingsHeader
            :heading="t('WASLO.ACTIVITY.TREND.HEADING')"
            :description="t('WASLO.ACTIVITY.TREND.DESCRIPTION')"
          />
          <TrendChart :points="reportData.trend" />
        </section>

        <!-- 3 · THE ONLY REGION WITH A VERB. -->
        <section class="flex flex-col gap-4">
          <SettingsHeader
            :heading="t('WASLO.ACTIVITY.QUESTIONS.HEADING')"
            :description="t('WASLO.ACTIVITY.QUESTIONS.DESCRIPTION')"
          />
          <UnansweredList
            :items="reportData.unanswered"
            :account-id="accountId"
            @fixed="report.reload()"
          />
        </section>

        <!-- 4 · WHAT IT ACTUALLY SAID. -->
        <section class="flex flex-col gap-4" data-waslo-activity-conversations>
          <SettingsHeader
            :heading="t('WASLO.ACTIVITY.CONVERSATIONS.HEADING')"
            :description="t('WASLO.ACTIVITY.CONVERSATIONS.DESCRIPTION')"
          />
          <ul
            v-if="reportData.conversations.length"
            class="flex flex-col w-full divide-y outline outline-1 -outline-offset-1 divide-n-weak outline-n-container rounded-xl bg-n-solid-2"
          >
            <TranscriptRow
              v-for="conversation in reportData.conversations"
              :key="conversation.id"
              :conversation="conversation"
              :account-id="accountId"
              :open="openConversationId === conversation.id"
              @toggle="toggleConversation(conversation.id)"
            />
          </ul>
          <p v-else class="mb-0 text-sm text-n-slate-11">
            {{ t('WASLO.ACTIVITY.CONVERSATIONS.NONE') }}
          </p>
        </section>
      </div>
    </template>
  </WasloPageLayout>
</template>
